import { type ReactElement } from 'react'
import { useForm } from 'react-hook-form'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useCreateV2Product } from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

type V2CreateProductFormValues = {
  name: string
  sku: string
  productModel: string
}

type V2CreateProductDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated: (productId: string) => void
}

export function V2CreateProductDialog({
  open,
  onOpenChange,
  onCreated,
}: V2CreateProductDialogProps): ReactElement {
  const { t } = useTranslation('common')
  const { mutateAsync: createProduct, isPending } = useCreateV2Product()

  const form = useForm<V2CreateProductFormValues>({
    defaultValues: {
      name: '',
      sku: '',
      productModel: '',
    },
  })

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      form.reset()
    }
    onOpenChange(nextOpen)
  }

  function validateName(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t('v2.pages.products.create.validation.nameRequired')
    }
    return true
  }

  function validateSku(value: string | undefined): true | string {
    if (value == null || value.trim().length === 0) {
      return t('v2.pages.products.create.validation.skuRequired')
    }
    return true
  }

  async function handleSubmit(values: V2CreateProductFormValues) {
    try {
      const product = await createProduct({
        name: values.name.trim(),
        sku: values.sku.trim(),
        productModel: values.productModel,
      })
      form.reset()
      onOpenChange(false)
      onCreated(product.id)
    } catch {
      toast.error(t('v2.pages.products.create.errors.submitFailed'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('v2.pages.products.create.title')}</DialogTitle>
          <DialogDescription>
            {t('v2.pages.products.create.description')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="space-y-4"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="name"
              rules={{ validate: validateName }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('v2.pages.products.create.nameLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.products.create.namePlaceholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sku"
              rules={{ validate: validateSku }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('v2.pages.products.create.skuLabel')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.products.create.skuPlaceholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="productModel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('v2.pages.products.create.productModelLabel')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      disabled={isPending}
                      placeholder={t(
                        'v2.pages.products.create.productModelPlaceholder'
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isPending}
                onClick={() => handleOpenChange(false)}
              >
                {t('actions.cancel')}
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending && (
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                )}
                {t('v2.pages.products.create.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
